import { useReducedMotion } from 'framer-motion';
import type { TargetAndTransition, Transition } from 'framer-motion';

const ERROR_EASE = [0.22, 1, 0.36, 1] as const;
const ERROR_DURATION_SECONDS = 0.45;
const ICON_ENTRANCE_DELAY_SECONDS = 0;
const TITLE_ENTRANCE_DELAY_SECONDS = 0.2;
const DESCRIPTION_ENTRANCE_DELAY_SECONDS = 0.4;

export type ErrorAnimationStep = {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  transition: Transition;
};

export type UseErrorAnimationResult = {
  reduceMotion: boolean;
  icon: ErrorAnimationStep;
  title: ErrorAnimationStep;
  description: ErrorAnimationStep;
};

const STATIC_STEP: ErrorAnimationStep = {
  initial: {},
  animate: {},
  transition: { duration: 0 },
};

function createFadeSlideStep(delaySeconds: number): ErrorAnimationStep {
  return {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: ERROR_DURATION_SECONDS,
      ease: ERROR_EASE,
      delay: delaySeconds,
    },
  };
}

function createIconStep(delaySeconds: number): ErrorAnimationStep {
  return {
    initial: { opacity: 0, scale: 0.85 },
    animate: { opacity: 1, scale: 1 },
    transition: {
      duration: ERROR_DURATION_SECONDS,
      ease: ERROR_EASE,
      delay: delaySeconds,
    },
  };
}

export function useErrorAnimation(): UseErrorAnimationResult {
  const reduceMotion = useReducedMotion() ?? false;

  if (reduceMotion) {
    return {
      reduceMotion: true,
      icon: STATIC_STEP,
      title: STATIC_STEP,
      description: STATIC_STEP,
    };
  }

  return {
    reduceMotion: false,
    icon: createIconStep(ICON_ENTRANCE_DELAY_SECONDS),
    title: createFadeSlideStep(TITLE_ENTRANCE_DELAY_SECONDS),
    description: createFadeSlideStep(DESCRIPTION_ENTRANCE_DELAY_SECONDS),
  };
}
